<template>
  <div :id="chartId"></div>
</template>

<script>
  export default {
    name: 'ActivityAuditBarChart',
    props: {
      chartId: {
        type: String,
        required: true
      },
      typeName: {
        type: String,
        required: true
      },
      activities: {
        type: Array,
        required: true
      }
    },
    data() {
      return {
        highCharts: null,
        chartInstance: null
      }
    },
    watch: {
      activities: {
        handler() {
          this.renderChart()
        },
        deep: true
      },
      highCharts() {
        if (this.highCharts) {
          this.renderChart()
        }
      },
      typeName() {
        this.renderChart()
      }
    },
    mounted() {
      import(/* webpackChunkName: "highcharts" */ 'highcharts').then(({ default: hc }) => {
        this.highCharts = hc
      })
    },
    beforeDestroy() {
      if (this.chartInstance) {
        try {
          this.chartInstance.destroy()
        } catch (e) {
          /* ignore */
        }
        this.chartInstance = null
      }
    },
    methods: {
      renderChart() {
        if (!this.highCharts) {
          return
        }

        if (this.chartInstance) {
          try {
            this.chartInstance.destroy()
          } catch (e) {
            /* ignore */
          }
          this.chartInstance = null
        }

        const data = this.activities || []
        if (data.length === 0) {
          return
        }

        const categories = data.map(item => item.name)
        const handledYes = data.map(item => Number(item.handledYes || 0))
        const handledNo = data.map(item => Number(item.handledNo || 0))

        const calculateChartHeight = cats => {
          const baseHeight = 200
          const barHeight = 65
          const marginPerBar = 20
          const headerHeight = 60
          const contentHeight = cats.length * (barHeight + marginPerBar) + headerHeight
          return Math.max(baseHeight, contentHeight)
        }
        const chartHeight = calculateChartHeight(categories)

        this.chartInstance = this.highCharts.chart(this.chartId, {
          credits: {
            enabled: false
          },
          chart: {
            type: 'bar',
            height: chartHeight
          },
          title: {
            text: this.typeName,
            style: {
              fontSize: '16px',
              fontFamily: 'Roboto, sans-serif'
            }
          },
          xAxis: {
            categories
          },
          yAxis: {
            min: 0,
            max: 100,
            allowDecimals: false,
            title: {
              text: null
            },
            labels: {
              enabled: false
            },
            gridLineWidth: 1,
            gridLineColor: '#e6e6e6',
            lineWidth: 0
          },
          legend: {
            reversed: true
          },
          tooltip: {
            shared: true,
            formatter: function () {
              const pts = this.points || (this.point ? [this.point] : [])
              if (!pts.length) {
                return false
              }
              let html = `<b>${this.x}</b><br/>`
              pts.forEach(p => {
                const pct =
                  p.percentage != null && !Number.isNaN(p.percentage) ? ` (${p.percentage.toFixed(1)}%)` : ''
                html += `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${p.y}</b>${pct}<br/>`
              })
              return html
            }
          },
          plotOptions: {
            bar: {
              cursor: 'pointer'
            },
            series: {
              stacking: 'percent',
              dataLabels: {
                enabled: false
              }
            }
          },
          series: [
            { name: 'Handled Correctly', data: handledYes, color: '#7cbe5f' },
            { name: 'Handled Incorrectly', data: handledNo, color: '#f56c6c' }
          ]
        })
      }
    }
  }
</script>

<style scoped>
  .highcharts-container {
    width: 100% !important;
    height: 100% !important;
  }

  .highcharts-root {
    width: 100% !important;
    height: 100% !important;
  }
</style>
